/** The phone's photo rules (apps/android PhotoUploader.kt `PhotoSizing`), so both upload the same kind of file. */
export const MAX_EDGE = 1600
export const JPEG_QUALITY = 0.85

/** The size to draw a [width] × [height] photo at: the long edge at most [maxEdge], never enlarged. */
export function targetSize(width: number, height: number, maxEdge = MAX_EDGE): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge || longest <= 0) return { width, height }
  const scale = maxEdge / longest
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

/**
 * The photo redrawn upright, shrunk and saved as a fresh JPEG. Redrawing keeps only the pixels, so EXIF and other
 * metadata (GPS location, camera, time) never leave the browser, as on the phone.
 */
export async function cleanPhoto(file: Blob): Promise<Blob> {
  // 'from-image' turns the photo upright by its EXIF orientation before that tag is dropped.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  try {
    const { width, height } = targetSize(bitmap.width, bitmap.height)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('This browser can’t prepare photos for upload.')
    context.drawImage(bitmap, 0, 0, width, height)
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('This browser can’t prepare photos for upload.'))),
        'image/jpeg',
        JPEG_QUALITY,
      ),
    )
  } finally {
    bitmap.close()
  }
}
